/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : useTabs.ts
// mainFrame의 탭(openTabs) 상태와 탭 생명주기 관리 훅
//  - 탭 추가/활성화/닫기(단일/전체/다른탭/좌측/우측)/새로고침/순서변경
//  - 초기 홈탭 자동생성, 언어 변경 시 탭 제목/메뉴경로 재번역
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import { useCallback, useEffect, useState } from 'react';
import type { TranslationFunction } from '../translations';
import type { useMsgBoxModal } from '../components/CommonModal';
import { hasUnsavedGrid } from '../components/AgGrid';
import { getActiveTask } from '../utils/activeTaskRegistry';

export interface TabItem {
  id: string;
  title: string;
  url: string;
  active: boolean;
  originalKey?: string; // 번역을 위한 원본 키
  menuPath?: string; // 메뉴 경로 정보
  parentMenuKey?: string; // 서브 메뉴의 경우 부모 메뉴 키
  paramData?: Record<string, any>; // JSON 형식으로 변경
  autoQuery?: boolean; // JumpIn하는 페이지에서 조회버튼 자동클릭
  icon?: string; // 아이콘 클래스
  reloadKey?: number; // 강제 리마운트를 위한 키
  linkKey?: string; // ZA_MENU.LINK_KEY - 'ADDTAB'이면 mainFrame이 ComponentRenderer 대신 IframeTab으로 url(외부 URL)을 그대로 띄움
}

interface UseTabsParams {
  ddKey: TranslationFunction;
  language: string;
  confirmMsgBox: ReturnType<typeof useMsgBoxModal>['confirmMsgBox'];
  closeDropdowns: () => void; // 탭관리/사용자 드롭다운을 닫기 위한 콜백
}

// 탭의 호출프로그램ID(callPgmId = CALL_PGM_ID = tab.url, 예: 'za/Z3001M1', 쿼리스트링 제외)
//  - MENU_ID(tab.id)는 메뉴트리 식별용 키일 뿐 화면의 callPgmId와 다를 수 있음(예: 어떤 메뉴는
//    MENU_ID='KZ301M1'인데 실제 페이지 컴포넌트의 callPgmId는 'za/Z3001M1') - AgGrid.tsx의 그리드
//    등록(registerGrid)과 각 화면의 window.closeTab(callPgmId)는 ComponentRenderer가 넘겨준 callPgmId(=tab.url)를 쓰므로,
//    "이 탭에 작업중(cud) 그리드가 있는지" 같은 callPgmId 기준 조회는 tab.id가 아니라 반드시 이 함수로 구한 값을 써야 함.
function tabCallPgmId(tab: Pick<TabItem, 'url'> | undefined): string {
  return (tab?.url || '').split('?')[0];
}

function makeHomeTab(ddKey: TranslationFunction): TabItem {
  return {
    id: 'Home',
    title: ddKey('Home') || 'Home',
    url: 'HomePage',
    originalKey: 'Home',
    menuPath: ddKey('Home') || 'Home',
    active: true,
  };
}

export function useTabs({ ddKey, language, confirmMsgBox, closeDropdowns }: UseTabsParams) {
  const [openTabs, setOpenTabs] = useState<TabItem[]>([]);

  // 언어 변경 시 탭 제목 갱신 - Home/za900xm1처럼 ZA_DD에 등록된 키를 쓰는 탭은 ddKey()로 재번역 가능하지만,
  // 일반 메뉴 탭(K1001M1 등)의 제목은 ZA_MENU_LANG(apiMainMenuTree) 응답에서 오는 것이라 ddKey()로는 재번역이 안 됨.
  // 그런 탭은 mainFrame이 메뉴트리를 새 언어로 재조회한 뒤 refreshTabTitles()를 호출해 최신 메뉴데이터로 갱신해야 함.
  const refreshTabTitles = useCallback((getTitle: (tab: TabItem) => { title: string; menuPath: string } | null) => {
    setOpenTabs(tabs =>
      tabs.map(tab => {
        const updated = getTitle(tab);
        return updated ? { ...tab, title: updated.title, menuPath: updated.menuPath } : tab;
      })
    );
  }, []);

  // 초기 홈페이지 로드
  useEffect(() => {
    // 탭이 없을 때 홈페이지 자동 열기 (메뉴 데이터 로드 성공 여부와 무관하게 mainFrame은 항상 열려야 함)
    if (openTabs.length === 0) {
      setOpenTabs([makeHomeTab(ddKey)]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openTabs.length, ddKey]);

  // 탭 관리 함수들
  const activateTab = useCallback((tabId: string) => {
    setOpenTabs(tabs => tabs.map(tab => ({ ...tab, active: tab.id === tabId })));
  }, []);

  // activate(기본 true) : false면 탭을 추가/보장만 하고 화면을 그 탭으로 전환하지 않음 - 홈 화면의 일정
  // 클릭처럼 "탭 전환 없이 팝업만" 띄우고 싶을 때, 대상 화면(X3001M1 등)을 백그라운드로 마운트만 해 둠
  // (그 화면이 자기 paramData로 여는 Dialog는 MUI Portal이라 document.body에 그려져서, 탭이 비활성
  // 상태라 보이지 않는 화면 영역과 무관하게 현재 보이는 화면 위에 그대로 뜸).
  const addTab = useCallback((tab: Omit<TabItem, 'active'> & { activate?: boolean }) => {
    const activate = tab.activate !== false;
    const { activate: _activate, ...tabRest } = tab;

    // 이미 같은 id의 탭이 있으면 활성화만(activate=false면 그대로 둠)
    const existingTab = openTabs.find(ot => ot.id === tab.id);
    if (existingTab) {
      if (activate) activateTab(tab.id);
      return;
    }

    // 탭 개수가 10개를 초과하면 가장 오래된 탭 제거 (Home 탭 제외)
    let newTabs = [...openTabs];
    if (newTabs.length >= 30) {
      // Home 탭이 아닌 가장 오래된 탭 제거
      const nonHomeTabs = newTabs.filter(ot => ot.id !== 'Home');
      const homeTabs = newTabs.filter(ot => ot.id === 'Home');
      newTabs = [...homeTabs, ...nonHomeTabs.slice(0, 8)];
    }

    // 모든 탭 비활성화 후 새 탭 추가 (현재 언어로 번역된 제목 사용) - activate=false면 기존 활성 탭을 그대로 둠
    if (activate) newTabs = newTabs.map(ot => ({ ...ot, active: false }));

    const newTab = {
      ...tabRest,
      title: ddKey(tab.originalKey || tab.id) || tab.title || tab.id,
      originalKey: tab.originalKey || tab.id,
      paramData: tab.paramData,
      autoQuery: tab.autoQuery,
      active: activate
    };

    // Home 탭이 있으면 두 번째 위치에, 없으면 첫 번째 위치에 추가
    const homeTabIndex = newTabs.findIndex(ot => ot.id === 'Home');
    if (homeTabIndex >= 0) {
      newTabs.splice(1, 0, newTab); // Home 다음 위치에 삽입
    } else {
      newTabs.unshift(newTab); // 첫 번째 위치에 삽입
    }
    setOpenTabs(newTabs);
  }, [openTabs, activateTab, ddKey]);

  const closeTab = useCallback(async (tabId: string) => {
    // Home 탭은 삭제 불가
    if (tabId === 'Home') return;

    // 화면 스스로 닫는 btnCloseTab(window.closeTab?.(callPgmId))은 tab.id(MENU_ID)가 아니라
    //  자신의 callPgmId(CALL_PGM_ID=tab.url)를 넘김 - MENU_ID와 다를 수 있으므로(예: MENU_ID='KA102M1', callPgmId='sd/T1101M1')
    //  id로 못 찾으면 tabCallPgmId 일치도 함께 확인.
    const targetTab = openTabs.find(tab => tab.id === tabId) ?? openTabs.find(tab => tabCallPgmId(tab) === tabId);
    if (!targetTab) return;
    const realTabId = targetTab.id;

    // 2026-09-11: 이 탭(callPgmId)에 등록된 그리드 중 작업중(cud 있음)인 행이 있으면 먼저 확인받음.
    //  - MENU_ID(tabId)가 아니라 tab.url(CALL_PGM_ID)에서 구한 programId로 확인해야 함(tabCallPgmId 참고).
    const closingCallPgmId = tabCallPgmId(targetTab);
    if (hasUnsavedGrid(closingCallPgmId)) {
      const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: ddKey('msgCloseTabUnsavedConfirm') || '저장하지 않은 변경사항이 있습니다. 그래도 닫으시겠습니까?', showBtn: "YesNo" });
      if (!confirmed) return;
    }

    // 2026-09-17: 백그라운드로 계속 진행 중인 작업(예: K2901B1 스크래핑)이 있으면 강제 종료 여부를 확인받음.
    const activeTask = getActiveTask(closingCallPgmId);
    if (activeTask) {
      const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: activeTask.message || '진행 중인 작업이 있습니다. 강제 종료하고 닫으시겠습니까?', showBtn: "YesNo" });
      if (!confirmed) return;
      activeTask.onForceClose?.();
    }

    setOpenTabs(prevTabs => {
      const tabIndex = prevTabs.findIndex(tab => tab.id === realTabId);
      if (tabIndex === -1) return prevTabs;

      const wasActive = prevTabs[tabIndex].active;
      const newTabs = prevTabs.filter(tab => tab.id !== realTabId);

      // 닫은 탭이 활성 탭이면 다른 탭을 활성화
      if (wasActive && newTabs.length > 0) {
        let newActiveIndex = tabIndex > 0 ? tabIndex - 1 : 0;

        // 이전 탭이 홈페이지이고 다음 탭이 있으면 다음 탭을 활성화
        if (newTabs[newActiveIndex]?.id === 'Home' && tabIndex < newTabs.length) {
          newActiveIndex = tabIndex;
        }

        // 인덱스가 유효한 범위 내인지 확인
        if (newActiveIndex >= newTabs.length) {
          newActiveIndex = newTabs.length - 1;
        }

        if (newTabs[newActiveIndex]) {
          newTabs[newActiveIndex].active = true;
        }
      }

      // 모든 탭이 닫혔으면 홈페이지 자동 열기
      if (newTabs.length === 0) {
        setTimeout(() => {
          setOpenTabs([makeHomeTab(ddKey)]);
        }, 100); // 약간의 지연을 두어 상태 업데이트가 완료된 후 실행
      }

      return newTabs;
    });
  }, [ddKey, confirmMsgBox, openTabs]);

  const closeAllTabs = useCallback(async () => {
    // Home 탭이 아닌 탭들의 개수
    const targetTabs = openTabs.filter(tab => tab.id !== 'Home');
    const nonHomeTabsCount = targetTabs.length;
    if (nonHomeTabsCount === 0) return;

    // 닫힐 탭 중 작업중(cud 있음)인 그리드가 있으면 경고 문구를 덧붙임
    const hasUnsaved = targetTabs.some(tab => hasUnsavedGrid(tabCallPgmId(tab)));
    const message = (ddKey('msgCloseAllTabsConfirm') || `${nonHomeTabsCount}개의 탭을 닫으시겠습니까?`)
      + (hasUnsaved ? (ddKey('msgCloseTabsUnsavedWarning') || ' (저장하지 않은 변경사항이 있는 탭이 포함되어 있습니다)') : '');
    const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: message, showBtn: "YesNo" });
    if (confirmed) {
      // Home 탭만 유지
      const homeTab = openTabs.find(tab => tab.id === 'Home');
      if (homeTab) {
        setOpenTabs([{ ...homeTab, active: true }]);
      } else {
        // Home 탭이 없으면 새로 생성
        setOpenTabs([makeHomeTab(ddKey)]);
      }
      closeDropdowns();
    }
  }, [openTabs, confirmMsgBox, ddKey, closeDropdowns]);

  const closeOtherTabs = useCallback(async () => {
    const activeTab = openTabs.find(tab => tab.active);
    if (!activeTab) return;

    // Home 탭과 현재 활성 탭 외의 탭 개수 계산
    const otherTabs = openTabs.filter(tab => tab.id !== activeTab.id && tab.id !== 'Home');
    const otherTabsCount = otherTabs.length;
    if (otherTabsCount === 0) return;

    const hasUnsaved = otherTabs.some(tab => hasUnsavedGrid(tabCallPgmId(tab)));
    const message = (ddKey('msgCloseOtherTabsConfirm') || '현재 탭을 제외한 {count}개의 탭을 닫으시겠습니까?')
      .replace('{count}', otherTabsCount.toString())
      + (hasUnsaved ? (ddKey('msgCloseTabsUnsavedWarning') || ' (저장하지 않은 변경사항이 있는 탭이 포함되어 있습니다)') : '');

    const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: message, showBtn: "YesNo" });
    if (confirmed) {
      // Home 탭과 현재 활성 탭만 유지
      const homeTab = openTabs.find(tab => tab.id === 'Home');
      const keepTabs = homeTab && homeTab.id !== activeTab.id ?
        [homeTab, activeTab] : [activeTab];
      setOpenTabs(keepTabs);
      closeDropdowns();
    }
  }, [openTabs, confirmMsgBox, ddKey, closeDropdowns]);

  const closeCurrentTab = useCallback(() => {
    const activeTab = openTabs.find(tab => tab.active);
    if (activeTab) {
      closeTab(activeTab.id);
    }
    closeDropdowns();
  }, [openTabs, closeTab, closeDropdowns]);

  const refreshAllTabs = useCallback(() => {
    // React 컴포넌트를 직접 렌더링하므로, 각 탭의 reloadKey를 증가시켜 리마운트 유도
    setOpenTabs(prev => prev.map(tab => ({ ...tab, reloadKey: (tab.reloadKey ?? 0) + 1 })));
    closeDropdowns();
  }, [closeDropdowns]);

  // 탭 내부에서 화면 전환(예: X1002Q1의 목록/상세 토글)에 맞춰 탭 타이틀만 갱신 - 새 탭을 만들지 않고
  // 같은 탭을 재활용하는 화면(window.setTabTitle로 노출)이 자신의 탭 이름을 스스로 바꿀 때 사용.
  const setTabTitle = useCallback((tabId: string, title: string) => {
    setOpenTabs(prev => prev.map(tab => (tab.id === tabId ? { ...tab, title } : tab)));
  }, []);

  const closeLeftTabs = useCallback(async () => {
    const activeTabIndex = openTabs.findIndex(tab => tab.active);
    if (activeTabIndex <= 0) return;

    // Home 탭을 제외한 왼쪽 탭들의 개수 계산
    const leftTabs = openTabs.slice(0, activeTabIndex);
    const leftTabsExceptHome = leftTabs.filter(tab => tab.id !== 'Home');
    const leftTabsCount = leftTabsExceptHome.length;

    if (leftTabsCount === 0) return;

    const hasUnsaved = leftTabsExceptHome.some(tab => hasUnsavedGrid(tabCallPgmId(tab)));
    const message = (ddKey('msgCloseLeftTabsConfirm') || '왼쪽의 {count}개 탭을 닫으시겠습니까?')
      .replace('{count}', leftTabsCount.toString())
      + (hasUnsaved ? (ddKey('msgCloseTabsUnsavedWarning') || ' (저장하지 않은 변경사항이 있는 탭이 포함되어 있습니다)') : '');

    const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: message, showBtn: "YesNo" });
    if (confirmed) {
      // Home 탭과 현재 활성 탭부터의 탭들만 유지
      const homeTab = openTabs.find(tab => tab.id === 'Home');
      const rightTabs = openTabs.slice(activeTabIndex);
      const newTabs = homeTab && !rightTabs.includes(homeTab) ? [homeTab, ...rightTabs] : rightTabs;
      setOpenTabs(newTabs);
      closeDropdowns();
    }
  }, [openTabs, confirmMsgBox, ddKey, closeDropdowns]);

  const closeRightTabs = useCallback(async () => {
    const activeTabIndex = openTabs.findIndex(tab => tab.active);
    if (activeTabIndex === -1 || activeTabIndex >= openTabs.length - 1) return;

    // Home 탭을 제외한 오른쪽 탭들의 개수 계산
    const rightTabs = openTabs.slice(activeTabIndex + 1);
    const rightTabsExceptHome = rightTabs.filter(tab => tab.id !== 'Home');
    const rightTabsCount = rightTabsExceptHome.length;

    if (rightTabsCount === 0) return;

    const hasUnsaved = rightTabsExceptHome.some(tab => hasUnsavedGrid(tabCallPgmId(tab)));
    const message = (ddKey('msgCloseRightTabsConfirm') || '오른쪽의 {count}개 탭을 닫으시겠습니까?')
      .replace('{count}', rightTabsCount.toString())
      + (hasUnsaved ? (ddKey('msgCloseTabsUnsavedWarning') || ' (저장하지 않은 변경사항이 있는 탭이 포함되어 있습니다)') : '');

    const confirmed = await confirmMsgBox({ title: ddKey('lblConfirm') || "확인", msgText: message, showBtn: "YesNo" });
    if (confirmed) {
      // Home 탭과 현재 활성 탭까지만 유지
      const homeTab = openTabs.find(tab => tab.id === 'Home');
      const leftTabs = openTabs.slice(0, activeTabIndex + 1);
      const newTabs = homeTab && !leftTabs.includes(homeTab) ? [homeTab, ...leftTabs] : leftTabs;
      setOpenTabs(newTabs);
      closeDropdowns();
    }
  }, [openTabs, confirmMsgBox, ddKey, closeDropdowns]);

  // 탭 순서 변경 함수
  const moveTab = useCallback((fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const newTabs = [...openTabs];
    const movedTab = newTabs[fromIndex];

    // Home 탭은 이동 불가
    if (movedTab.id === 'Home') return;

    // Home 탭이 있는 경우, 다른 탭들은 Home 다음 위치부터만 이동 가능
    const homeTabIndex = newTabs.findIndex(tab => tab.id === 'Home');
    if (homeTabIndex >= 0) {
      // toIndex가 Home 탭보다 앞에 오지 않도록 조정
      if (toIndex === 0) {
        toIndex = 1;
      }
    }

    const removedTab = newTabs.splice(fromIndex, 1)[0];
    newTabs.splice(toIndex, 0, removedTab);
    setOpenTabs(newTabs);
  }, [openTabs]);

  return {
    openTabs,
    activateTab,
    addTab,
    closeTab,
    closeAllTabs,
    closeOtherTabs,
    closeCurrentTab,
    refreshAllTabs,
    closeLeftTabs,
    closeRightTabs,
    moveTab,
    refreshTabTitles,
    setTabTitle,
  };
}
